import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Space, message, Spin, Upload, Select, Tag } from 'antd';
import { PlusOutlined, MinusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { HomeConfigFormData } from '../../types/home';
import homeService from '../../services/homeService';
import articleService, { ArticleSelector } from '../../services/articleService';
import collectionService, { CollectionSelector } from '../../services/collectionService';
import { uploadSingleFile, deleteFileByUrl } from '../../services/uploadService';

const { TextArea } = Input;
const { Option } = Select;

interface HomeConfigFormProps {
  onSave?: (data: HomeConfigFormData) => void;
  loading?: boolean;
}

// Component Upload cho một URL hình ảnh (click để thay thế)
const SingleImageUploadReplace: React.FC<{
  value?: string;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (value && typeof value === 'string' && value.trim() !== '') {
      setFileList([{
        uid: '-1',
        name: value.split('/').pop() || 'image',
        status: 'done',
        url: value,
        thumbUrl: value
      }]);
    } else {
      setFileList([]);
    }
  }, [value]);

  const handleUpload = async ({ file, fileList }: any) => {
    try {
      if (file.status === 'uploading') {
        setUploading(true);
        // Xóa ảnh cũ trước khi upload ảnh mới
        if (value) {
          try {
            await deleteFileByUrl(value);
          } catch (error) {
            console.error('Failed to delete old image:', error);
          }
        }

        const response = await uploadSingleFile(file.originFileObj);
        if (response.status === 'success') {
          const updatedFileList = fileList.map((f: any) => {
            if (f.uid === file.uid) {
              return {
                ...f,
                url: response.data.file.url,
                thumbUrl: response.data.file.url,
                status: 'done'
              };
            }
            return f;
          });
          setFileList(updatedFileList);
          onChange?.(response.data.file.url);
          message.success('Upload ảnh thành công');
        }
        setUploading(false);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      message.error('Upload thất bại');
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!value) return;

    try {
      setUploading(true);
      const response = await deleteFileByUrl(value);
      if (response.status === 'success') {
        setFileList([]);
        onChange?.(undefined);
        message.success('Xóa ảnh thành công');
      } else {
        message.error('Xóa ảnh thất bại');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Xóa ảnh thất bại');
    } finally {
      setUploading(false);
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const imagePreview = (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <img
        src={value}
        alt="preview"
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'cover',
          borderRadius: '6px',
          width: '100%',
          height: '100%'
        }}
      />
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        size="small"
        loading={uploading}
        onClick={(e) => {
          e.stopPropagation();
          handleDelete();
        }}
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 1
        }}
      />
    </div>
  );

  return (
    <Upload
      listType="picture-card"
      fileList={fileList}
      customRequest={({ file, onSuccess }: any) => {
        setTimeout(() => {
          onSuccess("ok");
        }, 0);
      }}
      onChange={handleUpload}
      maxCount={1}
      disabled={uploading}
      style={{
        width: '100%',
        maxWidth: '200px'
      }}
      showUploadList={false}
    >
      {value ? imagePreview : uploadButton}
    </Upload>
  );
};



// Component Upload cho nhiều URL hình ảnh
const MultipleImageUpload: React.FC<{
  value?: string[];
  onChange?: (value: string[]) => void;
  maxCount?: number;
}> = ({ value = [], onChange, maxCount = 10 }) => {
  const [fileList, setFileList] = useState<any[]>([]);

  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      setFileList(value
        .filter(url => typeof url === 'string' && url.trim() !== '')
        .map((url, index) => ({
          uid: `-${index}`,
          name: url.split('/').pop() || `image-${index}`,
          status: 'done',
          url: url,
          thumbUrl: url
        })));
    } else {
      setFileList([]);
    }
  }, [value]);

  const handleUpload = async ({ file, fileList }: any) => {
    try {
      if (file.status === 'uploading') {
        const response = await uploadSingleFile(file.originFileObj);
        if (response.status === 'success') {
          const updatedFileList = fileList.map((f: any) => {
            if (f.uid === file.uid) {
              return {
                ...f,
                url: response.data.file.url,
                thumbUrl: response.data.file.url,
                status: 'done'
              };
            }
            return f;
          });
          setFileList(updatedFileList);
          onChange?.(updatedFileList.map((f: any) => f.url));
        }
      } else if (file.status === 'removed') {
        try {
          const response = await deleteFileByUrl(file.url);
          if (response.status === 'success') {
            const remainingFiles = fileList.filter((f: any) => f.uid !== file.uid);
            setFileList(remainingFiles);
            onChange?.(remainingFiles.map((f: any) => f.url));
            message.success('File deleted successfully');
          } else {
            message.error('Failed to delete file');
          }
        } catch (error) {
          console.error('Delete failed:', error);
          message.error('Failed to delete file');
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      message.error('Upload failed');
    }
  };

  const handleRemove = async (file: any) => {
    try {
      const response = await deleteFileByUrl(file.url);
      if (response.status === 'success') {
        const newFileList = fileList.filter((f: any) => f.url !== file.url);
        setFileList(newFileList);
        onChange?.(newFileList.map((f: any) => f.url));
        message.success('File deleted successfully');
      } else {
        message.error('Failed to delete file');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Failed to delete file');
    }
    return true;
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Upload
      listType="picture-card"
      fileList={fileList}
      customRequest={({ file, onSuccess }: any) => {
        setTimeout(() => {
          onSuccess("ok");
        }, 0);
      }}
      onChange={handleUpload}
      onRemove={handleRemove}
      multiple
      maxCount={maxCount}
      style={{ width: '100%' }}
    >
      {fileList.length >= maxCount ? null : uploadButton}
    </Upload>
  );
};

const HomeConfigForm: React.FC<HomeConfigFormProps> = ({ onSave, loading = false }) => {
  const [form] = Form.useForm();
  const [initialLoading, setInitialLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [articles, setArticles] = useState<ArticleSelector[]>([]);
  const [collections, setCollections] = useState<CollectionSelector[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [productCount, setProductCount] = useState(4);

  const defaultImageUrl = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN';

  const loadHomeConfig = useCallback(async () => {
    try {
      setInitialLoading(true);
      const config = await homeService.getHomeConfig();
      console.log('Loaded home config:', config);
      form.setFieldsValue(config);

      // Khởi tạo state cho selected items
      setSelectedArticles(config.news_updates || []);
      setSelectedCollections(config.collections || []);

      // Đảm bảo hero.image_urls có cấu trúc đúng
      if (config.hero) {
        if (!config.hero.image_urls) {
          config.hero.image_urls = [];
        } else if (Array.isArray(config.hero.image_urls)) {
          // Đảm bảo tất cả phần tử đều có cấu trúc { url: string, description: string }
          config.hero.image_urls = config.hero.image_urls.map((item: any) => {
            if (typeof item === 'string') {
              return { url: item, description: '' };
            }
            return {
              url: item?.url || '',
              description: item?.description || ''
            };
          });
        }
      }

      // Đảm bảo cultural_mission.image_urls có cấu trúc đúng
      if (config.cultural_mission && !config.cultural_mission.image_urls) {
        config.cultural_mission.image_urls = [
          { url: '', description: '' },
          { url: '', description: '' },
          { url: '', description: '' },
          { url: '', description: '' }
        ];
      }
    } catch (error) {
      message.error('Không thể tải cấu hình trang chủ');
      console.error('Error loading home config:', error);
    } finally {
      setInitialLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadHomeConfig();
    fetchArticles();
    fetchCollections();
  }, [loadHomeConfig]);

  const fetchArticles = async () => {
    try {
      setArticlesLoading(true);
      const result = await articleService.getArticleSelectors();
      setArticles(result.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      message.error('Lỗi khi tải danh sách bài viết');
    } finally {
      setArticlesLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      setCollectionsLoading(true);
      const result = await collectionService.getCollectionSelectors();
      setCollections(result.data);
    } catch (error) {
      console.error('Error fetching collections:', error);
      message.error('Lỗi khi tải danh sách bộ sưu tập');
    } finally {
      setCollectionsLoading(false);
    }
  };

  const handleSave = async (values: HomeConfigFormData) => {
    try {
      setSaveLoading(true);

      // Cập nhật values với dữ liệu từ state local
      const updatedValues = {
        ...values,
        collections: selectedCollections,
        news_updates: selectedArticles
      };

      console.log("thongnv handleSave", updatedValues)

      // Validate JSON format
      const jsonString = JSON.stringify(updatedValues, null, 2);
      try {
        JSON.parse(jsonString);
      } catch (e) {
        message.error('Dữ liệu không đúng định dạng JSON');
        setSaveLoading(false);
        return;
      }

      const result = await homeService.updateHomeConfig(updatedValues);
      console.log('Update result:', result);
      
      // Không hiển thị message ở đây, để callback parent component xử lý
      // message.success('Cập nhật cấu hình trang chủ thành công!');

      // Gọi callback onSave sau khi lưu thành công
      if (onSave) {
        onSave(updatedValues);
      }
    } catch (error: any) {
      console.error('Error updating home config:', error);
      
      // Hiển thị thông báo lỗi chi tiết
      const errorMessage = error?.message || 'Không thể cập nhật cấu hình trang chủ';
      message.error(errorMessage);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    loadHomeConfig();
  };

  const addProduct = () => {
    setProductCount(prev => prev + 1);
  };

  const removeProduct = (index: number) => {
    if (productCount > 1) {
      setProductCount(prev => prev - 1);
      // Xóa dữ liệu của sản phẩm bị xóa
      const currentValues = form.getFieldsValue();
      if (currentValues.curated_for_you && currentValues.curated_for_you[index]) {
        delete currentValues.curated_for_you[index];
        form.setFieldsValue(currentValues);
      }
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
      >
        {/* Hero Section */}
        <Card
          title="Phần Hero"
          style={{
            marginBottom: 24,
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {/* Nội dung text - Phần trên */}
          <div style={{ marginBottom: 24 }}>
              {/* Tiêu đề chính */}
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: '#fafafa',
                marginBottom: 16
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['hero', 'title']}
                      label="Tiêu đề chính (Tiếng Việt)"
                      rules={[{ required: true, message: 'Vui lòng nhập tiêu đề chính' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="VD: DE SILK"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['hero', 'title_en']}
                      label="Tiêu đề chính (Tiếng Anh)"
                      rules={[{ required: true, message: 'Vui lòng nhập tiêu đề chính tiếng Anh' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Enter main title in English"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* Phụ đề */}
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: '#fafafa',
                marginBottom: 16
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['hero', 'subtitle']}
                      label="Tiêu đề phụ (Tiếng Việt)"
                      rules={[{ required: true, message: 'Vui lòng nhập tiêu đề phụ' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="VD: FROM VIETNAM OUT THERE - INSPIRING WORLD-CLASS PREMIUM"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['hero', 'subtitle_en']}
                      label="Tiêu đề phụ (Tiếng Anh)"
                      rules={[{ required: true, message: 'Vui lòng nhập tiêu đề phụ tiếng Anh' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Enter subtitle in English"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* Nút CTA */}
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['hero', 'button_text']}
                      label="Nút hành động (Tiếng Việt)"
                      rules={[{ required: true, message: 'Vui lòng nhập text nút' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="VD: EXPLORE"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['hero', 'button_text_en']}
                      label="Nút hành động (Tiếng Anh)"
                      rules={[{ required: true, message: 'Vui lòng nhập text nút tiếng Anh' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Enter button text in English"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
          </div>

          {/* Upload ảnh - Phần dưới */}
          <div>
            <Form.List name={['hero', 'image_urls']}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card key={key} size="small" style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'url']}
                          rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <SingleImageUploadReplace />
                        </Form.Item>
                        <div style={{ 
                          flex: 1, 
                          width: '100%',         
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          padding: '16px',
                          backgroundColor: '#fafafa',
                        }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'description']}
                            label="Mô tả ảnh"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                            style={{ marginBottom: 0, flex: 1 }}
                          >
                            <Input
                              placeholder="Nhập mô tả cho ảnh này..."
                              size="small"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'description_en']}
                            label="Mô tả ảnh (tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                            style={{ marginBottom: 0, flex: 1 }}
                          >
                            <Input
                              placeholder="Enter image description in English..."
                              size="small"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          size="small"
                        />
                      </div>
                      {/* <div style={{ marginLeft: '102px', marginTop: '8px' }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'description_en']}
                          label="Mô tả ảnh (tiếng Anh)"
                          rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                          style={{ marginBottom: '8px' }}
                        >
                          <Input
                            placeholder="Enter image description in English..."
                            size="small"
                            style={{
                              height: 32,
                              backgroundColor: '#f5f5f5',
                            }}
                          />
                        </Form.Item>
                      </div> */}
                    </Card>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      disabled={fields.length >= 5}
                    >
                      Thêm banner
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        </Card>

        {/* Intro Section */}
        <Card
          title="Phần Giới thiệu"
          style={{
            marginBottom: 24,
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {/* Tiêu đề */}
          <div style={{
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: '#fafafa',
            marginBottom: 16
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['intro', 'title']}
                  label="Tiêu đề (Tiếng Việt)"
                  rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="VD: TINH HOA DI SẢN TRONG TỪNG SỢI LỤA"
                    style={{
                      width: '100%',
                      height: 32,
                      borderRadius: 4,
                      fontSize: 16,
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['intro', 'title_en']}
                  label="Tiêu đề (Tiếng Anh)"
                  rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Enter title in English"
                    style={{
                      width: '100%',
                      height: 32,
                      borderRadius: 4,
                      fontSize: 16,
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {/* Mô tả */}
          <div style={{
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: '#fafafa',
            marginBottom: 16
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['intro', 'description']}
                  label="Mô tả (Tiếng Việt)"
                  rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={6}
                    placeholder="VD: DeSilk được khơi nguồn từ niềm kiêu hãnh đối với lụa Việt – chất liệu gắn bó sâu sắc với đời sống và tâm hồn dân tộc suốt hàng nghìn năm."
                    style={{
                      resize: 'none',
                      fontSize: '14px'
                    }}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['intro', 'description_en']}
                  label="Mô tả (Tiếng Anh)"
                  rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={6}
                    placeholder="Enter description in English"
                    style={{
                      resize: 'none',
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {/* Upload ảnh - Đặt dưới cùng */}
          <Form.Item
            name={['intro', 'images']}
            label="Hình ảnh (tối đa 3 ảnh)"
            rules={[{ required: true, message: 'Vui lòng chọn ít nhất 1 hình ảnh' }]}
            style={{ marginBottom: 0 }}
          >
            <MultipleImageUpload maxCount={3} />
          </Form.Item>
        </Card>

        {/* Features Intro Section */}
        <Card
          title="Phần Giới thiệu Tính năng"
          style={{
            marginBottom: 24,
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <Form.Item
                name={['features_intro', 'title']}
                label="Tiêu đề"
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                style={{ marginBottom: 16 }}
              >
                <Input
                  placeholder="Nhập tiêu đề giới thiệu tính năng..."
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
              <Form.Item
                name={['features_intro', 'description']}
                label="Mô tả"
                rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                style={{ marginBottom: 16 }}
              >
                <TextArea
                  rows={6}
                  placeholder="Nhập mô tả giới thiệu tính năng..."
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
            </div>
            <div style={{ flex: 1 }}>
              <Form.Item
                name={['features_intro', 'title_en']}
                label="Tiêu đề (tiếng Anh)"
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                style={{ marginBottom: 16 }}
              >
                <Input
                  placeholder="Enter features intro title in English..."
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
              <Form.Item
                name={['features_intro', 'description_en']}
                label="Mô tả (tiếng Anh)"
                rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                style={{ marginBottom: 16 }}
              >
                <TextArea
                  rows={6}
                  placeholder="Enter features intro description in English..."
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
            </div>
          </div>
        </Card>

        {/* Features Section */}
        <Card
          title="Phần Tính năng nổi bật"
          style={{
            marginBottom: 24,
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Form.List name="features">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ marginBottom: 24 }}>
                    <Card
                      size="small"
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                        {/* Upload ảnh - Cột trái */}
                        <div style={{ minWidth: '200px' }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'image_url']}
                            label="Hình ảnh"
                            rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <SingleImageUploadReplace />
                          </Form.Item>
                        </div>

                        {/* Nội dung text - Cột phải */}
                        <div style={{ flex: 1 }}>
                          {/* Tiêu đề và Mô tả ngắn */}
                          <div style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            padding: '16px',
                            backgroundColor: '#ffffff',
                            marginBottom: 16
                          }}>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                              <div style={{ flex: 1 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'title']}
                                  label="Tiêu đề (Tiếng Việt)"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input
                                    placeholder="VD: Ngôn ngữ thiết kế độc đáo"
                                    style={{
                                      width: '100%',
                                      height: 32,
                                      borderRadius: 4,
                                      fontSize: 16,
                                      padding: '6px 12px',
                                      backgroundColor: '#f5f5f5',
                                    }}
                                  />
                                </Form.Item>
                              </div>
                              <div style={{ flex: 1 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'title_en']}
                                  label="Tiêu đề (Tiếng Anh)"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input
                                    placeholder="Enter title in English"
                                    style={{
                                      width: '100%',
                                      height: 32,
                                      borderRadius: 4,
                                      fontSize: 16,
                                      padding: '6px 12px',
                                      backgroundColor: '#f5f5f5',
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 16 }}>
                              <div style={{ flex: 1 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'description']}
                                  label="Mô tả ngắn (Tiếng Việt)"
                                  rules={[{ required: true, message: 'Vui lòng nhập mô tả ngắn' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input
                                    placeholder="VD: Ngôn ngữ thiết kế độc đáo"
                                    style={{
                                      width: '100%',
                                      height: 32,
                                      borderRadius: 4,
                                      fontSize: 16,
                                      padding: '6px 12px',
                                      backgroundColor: '#f5f5f5',
                                    }}
                                  />
                                </Form.Item>
                              </div>
                              <div style={{ flex: 1 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'description_en']}
                                  label="Mô tả ngắn (Tiếng Anh)"
                                  rules={[{ required: true, message: 'Vui lòng nhập mô tả ngắn tiếng Anh' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input
                                    placeholder="Enter short description in English"
                                    style={{
                                      width: '100%',
                                      height: 32,
                                      borderRadius: 4,
                                      fontSize: 16,
                                      padding: '6px 12px',
                                      backgroundColor: '#f5f5f5',
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </div>
                          </div>

                          {/* Chi tiết */}
                          <div style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            padding: '16px',
                            backgroundColor: '#ffffff',
                            marginBottom: 16
                          }}>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                              <div style={{ flex: 1 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'detail']}
                                  label="Chi tiết (Tiếng Việt)"
                                  rules={[{ required: true, message: 'Vui lòng nhập chi tiết' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <TextArea
                                    rows={6}
                                    placeholder="VD: Thiết kế DeSilk mang dấu ấn độc đáo: lấy cảm hứng từ văn hóa Việt, hòa quyện tinh thần Đông – Tây, để mỗi đường nét đều gợi cảm xúc, biến sản phẩm thành một tác phẩm nghệ thuật không thể trộn lẫn"
                                    style={{
                                      resize: 'none',
                                      fontSize: '14px'
                                    }}
                                  />
                                </Form.Item>
                              </div>
                              <div style={{ flex: 1 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'detail_en']}
                                  label="Chi tiết (Tiếng Anh)"
                                  rules={[{ required: true, message: 'Vui lòng nhập chi tiết tiếng Anh' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <TextArea
                                    rows={6}
                                    placeholder="Enter details in English"
                                    style={{
                                      resize: 'none',
                                      fontSize: '14px',
                                      backgroundColor: '#f5f5f5',
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            </div>
                          </div>

                          {/* Link */}
                          <div style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            padding: '16px',
                            backgroundColor: '#ffffff'
                          }}>
                            <Form.Item
                              {...restField}
                              name={[name, 'link']}
                              label="Link"
                              rules={[
                                {
                                  type: 'url',
                                  message: 'Vui lòng nhập URL hợp lệ'
                                }
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                placeholder="VD: https://desilk.com.vn/about-us"
                                style={{
                                  width: '100%',
                                  height: 32,
                                  borderRadius: 4,
                                  fontSize: 16,
                                  padding: '6px 12px',
                                  backgroundColor: '#f5f5f5',
                                }}
                              />
                            </Form.Item>
                          </div>
                        </div>

                        {/* Nút xóa */}
                        <div style={{ marginTop: 32 }}>
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          />
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Thêm tính năng
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Card>

        {/* Cultural Mission Section */}
        <Card
          title="Phần Sứ mệnh văn hóa"
          style={{
            marginBottom: 24,
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            {/* Upload ảnh - Cột trái */}
            <div style={{ minWidth: '250px' }}>
              <Form.Item
                name={['cultural_mission', 'main_image_url']}
                label="Hình ảnh chính"
                rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                style={{ marginBottom: 0 }}
              >
                <SingleImageUploadReplace />
              </Form.Item>
            </div>

            {/* Nội dung text - Cột phải */}
            <div style={{ flex: 1 }}>
              {/* Tiêu đề và Nút hành động */}
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: '#fafafa',
                marginBottom: 16
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['cultural_mission', 'title']}
                      label="Tiêu đề (Tiếng Việt)"
                      rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="VD: TINH HOA KIẾN TẠO"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['cultural_mission', 'title_en']}
                      label="Tiêu đề (Tiếng Anh)"
                      rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Enter title in English"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 16 }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['cultural_mission', 'button_text']}
                      label="Nút hành động (Tiếng Việt)"
                      rules={[{ required: true, message: 'Vui lòng nhập text nút' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="VD: ĐỌC THÊM"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['cultural_mission', 'button_text_en']}
                      label="Nút hành động (Tiếng Anh)"
                      rules={[{ required: true, message: 'Vui lòng nhập text nút tiếng Anh' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Enter button text in English"
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* Mô tả */}
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: '#fafafa',
                marginBottom: 16
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['cultural_mission', 'description']}
                      label="Mô tả (Tiếng Việt)"
                      rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea
                        rows={6}
                        placeholder="VD: Từ mạch nguồn di sản, DeSilk kiến tạo hành trình sáng tạo, nơi lụa trở thành ngôn ngữ của nghệ thuật, văn hóa và thời trang đương đại. Chúng tôi kiên định với bốn giá trị nền tảng"
                        style={{
                          resize: 'none',
                          fontSize: '14px'
                        }}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name={['cultural_mission', 'description_en']}
                      label="Mô tả (Tiếng Anh)"
                      rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea
                        rows={6}
                        placeholder="Enter description in English"
                        style={{
                          resize: 'none',
                          fontSize: '14px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* Link */}
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: '#fafafa'
              }}>
                <Form.Item
                  name={['cultural_mission', 'link']}
                  label="Link"
                  rules={[
                    {
                      type: 'url',
                      message: 'Vui lòng nhập URL hợp lệ'
                    }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="VD: https://desilk.com.vn/about-us"
                    style={{
                      width: '100%',
                      height: 32,
                      borderRadius: 4,
                      fontSize: 16,
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {/* 4 hình ảnh với mô tả - Đặt dưới cùng */}
          <div style={{ marginBottom: 24 }}>
            <Card
              size="small"
              style={{
                marginBottom: 16,
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Upload ảnh */}
                <div style={{ minWidth: '200px' }}>
                  <Form.Item
                    name={['cultural_mission', 'image_urls', 0, 'url']}
                    label="Hình ảnh 1"
                    rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <SingleImageUploadReplace />
                  </Form.Item>
                </div>

                {/* Nội dung text */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: '#fafafa'
                  }}>
                    {/* Tiêu đề */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 0, 'title']}
                            label="Tiêu đề (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="VD: Di sản"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 0, 'title_en']}
                            label="Tiêu đề (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="Enter image 1 title in English"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>

                    {/* Mô tả */}
                    <div>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 0, 'description']}
                            label="Mô tả (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="VD: Gìn giữ tinh hoa thủ công lụa Việt, lấy truyền thống làm nền để nuôi dưỡng sáng tạo bền vững."
                              rows={6}
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 0, 'description_en']}
                            label="Mô tả (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="Enter image 1 description in English"
                              rows={6}
                              style={{
                                resize: 'none',
                                fontSize: '14px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Card
              size="small"
              style={{
                marginBottom: 16,
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Upload ảnh */}
                <div style={{ minWidth: '200px' }}>
                  <Form.Item
                    name={['cultural_mission', 'image_urls', 1, 'url']}
                    label="Hình ảnh 2"
                    rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <SingleImageUploadReplace />
                  </Form.Item>
                </div>

                {/* Nội dung text */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: '#fafafa'
                  }}>
                    {/* Tiêu đề */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 1, 'title']}
                            label="Tiêu đề (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="VD: Tinh tế"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 1, 'title_en']}
                            label="Tiêu đề (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="Enter image 2 title in English"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>

                    {/* Mô tả */}
                    <div>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 1, 'description']}
                            label="Mô tả (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="VD: Chắt lọc từng chi tiết – từ nguyên liệu thượng hạng đến thiết kế nghệ thuật – để kiến tạo vẻ đẹp thanh nhã và giàu chiều sâu."
                              rows={6}
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 1, 'description_en']}
                            label="Mô tả (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="Enter image 2 description in English"
                              rows={6}
                              style={{
                                resize: 'none',
                                fontSize: '14px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Card
              size="small"
              style={{
                marginBottom: 16,
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Upload ảnh */}
                <div style={{ minWidth: '200px' }}>
                  <Form.Item
                    name={['cultural_mission', 'image_urls', 2, 'url']}
                    label="Hình ảnh 3"
                    rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <SingleImageUploadReplace />
                  </Form.Item>
                </div>

                {/* Nội dung text */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: '#fafafa'
                  }}>
                    {/* Tiêu đề */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 2, 'title']}
                            label="Tiêu đề (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="VD: Chân thực"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 2, 'title_en']}
                            label="Tiêu đề (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="Enter image 3 title in English"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>

                    {/* Mô tả */}
                    <div>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 2, 'description']}
                            label="Mô tả (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="VD: Cam kết minh bạch trong chất liệu, quy trình và bản sắc, để mỗi sản phẩm là niềm tin và giá trị thật."
                              rows={6}
                              style={{
                                resize: 'none',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 2, 'description_en']}
                            label="Mô tả (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="Enter image 3 description in English"
                              rows={6}
                              style={{
                                resize: 'none',
                                fontSize: '14px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Card
              size="small"
              style={{
                marginBottom: 16,
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Upload ảnh */}
                <div style={{ minWidth: '200px' }}>
                  <Form.Item
                    name={['cultural_mission', 'image_urls', 3, 'url']}
                    label="Hình ảnh 4"
                    rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <SingleImageUploadReplace />
                  </Form.Item>
                </div>

                {/* Nội dung text */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: '#fafafa'
                  }}>
                    {/* Tiêu đề */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 3, 'title']}
                            label="Tiêu đề (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="VD: Trường tồn"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 3, 'title_en']}
                            label="Tiêu đề (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="Enter image 4 title in English"
                              style={{
                                width: '100%',
                                height: 32,
                                borderRadius: 4,
                                fontSize: 16,
                                padding: '6px 12px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>

                    {/* Mô tả */}
                    <div>
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 3, 'description']}
                            label="Mô tả (Tiếng Việt)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="VD: Vượt trên xu hướng, mỗi thiết kế là tác phẩm sống cùng thời gian, mang lại cảm xúc và đẳng cấp bền vững."
                              rows={6}
                              style={{
                                resize: 'none',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={['cultural_mission', 'image_urls', 3, 'description_en']}
                            label="Mô tả (Tiếng Anh)"
                            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              placeholder="Enter image 4 description in English"
                              rows={6}
                              style={{
                                resize: 'none',
                                fontSize: '14px',
                                backgroundColor: '#f5f5f5',
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </Card>

        {/* Products Section */}
        <Card
          title="Phần Sản phẩm"
          style={{
            marginBottom: 24,
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {/* Tiêu đề */}
          <div style={{
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: '#fafafa',
            marginBottom: 16
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['products', 'title']}
                  label="Tiêu đề (Tiếng Việt)"
                  rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="VD: SẢN PHẨM"
                    style={{
                      width: '100%',
                      height: 32,
                      borderRadius: 4,
                      fontSize: 16,
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['products', 'title_en']}
                  label="Tiêu đề (Tiếng Anh)"
                  rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Enter title in English"
                    style={{
                      width: '100%',
                      height: 32,
                      borderRadius: 4,
                      fontSize: 16,
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {/* Mô tả */}
          <div style={{
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: '#fafafa'
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['products', 'description']}
                  label="Mô tả (Tiếng Việt)"
                  rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={6}
                    placeholder="VD: Phụ kiện và trang phục từ 100% tơ tằm tự nhiên thượng hạng – Chạm vào lụa & trải nghiệm đỉnh cao của sự tinh tế"
                    style={{
                      resize: 'none',
                      fontSize: '14px'
                    }}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1 }}>
                <Form.Item
                  name={['products', 'description_en']}
                  label="Mô tả (Tiếng Anh)"
                  rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={6}
                    placeholder="Enter description in English"
                    style={{
                      resize: 'none',
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </Card>

      {/* Curated for You Section */}
      <Card
        title="Phần Sản phẩm được chọn"
        style={{
          marginBottom: 24,
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        {Array.from({ length: productCount }, (_, index) => (
          <div key={index} style={{ marginBottom: 16 }}>
            <Card
              size="small"
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                backgroundColor: '#fafafa'
              }}
            >
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Upload ảnh - Cột trái */}
                <div style={{ minWidth: '200px' }}>
                  <Form.Item
                    name={['curated_for_you', index, 'image_url']}
                    label={`Hình ảnh sản phẩm ${index + 1}`}
                    rules={[{ required: true, message: 'Vui lòng chọn hình ảnh' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <SingleImageUploadReplace />
                  </Form.Item>
                </div>

                {/* Nội dung text - Cột phải */}
                <div style={{ flex: 1 }}>
                  {/* Caption */}
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: '#ffffff',
                    marginBottom: 16
                  }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <Form.Item
                          name={['curated_for_you', index, 'caption']}
                          label={`Chú thích (Tiếng Việt)`}
                          rules={[{ required: true, message: 'Vui lòng nhập chú thích' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder={`VD: Váy hai dây lụa Hanoi Bloomscape`}
                            style={{
                              width: '100%',
                              height: 32,
                              borderRadius: 4,
                              fontSize: 16,
                              padding: '6px 12px',
                              backgroundColor: '#f5f5f5',
                            }}
                          />
                        </Form.Item>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Form.Item
                          name={['curated_for_you', index, 'caption_en']}
                          label={`Chú thích (Tiếng Anh)`}
                          rules={[{ required: true, message: 'Vui lòng nhập chú thích tiếng Anh' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder={`Enter product ${index + 1} caption in English`}
                            style={{
                              width: '100%',
                              height: 32,
                              borderRadius: 4,
                              fontSize: 16,
                              padding: '6px 12px',
                              backgroundColor: '#f5f5f5',
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </div>

                  {/* URL */}
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: '#ffffff'
                  }}>
                    <Form.Item
                      name={['curated_for_you', index, 'url']}
                      label={`URL sản phẩm ${index + 1}`}
                      rules={[
                        { required: true, message: 'Vui lòng nhập URL' },
                        { type: 'url', message: 'Vui lòng nhập URL hợp lệ' }
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={`VD: https://desilk.com.vn/product/vay-hai-day-lua-hanoi-bloomscape-hoa-tiet-hoa-thuoc-duoc`}
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px',
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Nút xóa */}
                {productCount > 1 && (
                  <div style={{ marginTop: 32 }}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeProduct(index)}
                    />
                  </div>
                )}
              </div>
            </Card>
          </div>
        ))}

        <Button
          type="dashed"
          onClick={addProduct}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginTop: 8 }}
        >
          Thêm sản phẩm
        </Button>
      </Card>

      {/* News Updates Section */}
      <Card
        title="Phần Tin tức cập nhật"
        style={{
          marginBottom: 24,
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Form.Item
           name="news_updates"
           label="Chọn bài viết (tối đa 5 bài)"
           rules={[{ required: true, message: 'Vui lòng chọn ít nhất 1 bài viết' }]}
         >
          <Select
            mode="multiple"
            placeholder="Chọn bài viết"
            allowClear
            showSearch
            loading={articlesLoading}
                         maxTagCount={5}
            maxTagTextLength={20}
            value={selectedArticles}
            optionFilterProp="title"
            filterOption={(input, option) =>
              String((option as any)?.title || '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(values) => {
              // Cập nhật state local
              setSelectedArticles(values || []);

                // Giới hạn tối đa 5 bài viết
               if (values && values.length > 5) {
                 const limitedValues = values.slice(0, 5);
                 form.setFieldsValue({ news_updates: limitedValues });
                 setSelectedArticles(limitedValues);
                 message.warning('Chỉ có thể chọn tối đa 5 bài viết');
               }
               // Thông báo khi đủ 5 bài viết
               if (values && values.length === 5) {
                 message.success('Đã chọn đủ 5 bài viết!');
               }
               // Thông báo khi xóa bài viết và có thể chọn thêm
               if (values && values.length < 5) {
                const currentValues = selectedArticles;
                if (currentValues.length > values.length) {
                  message.info('Đã xóa bài viết, có thể chọn thêm bài viết khác!');
                }
              }
            }}
                         onDropdownVisibleChange={(open) => {
               if (open) {
                 if (selectedArticles.length >= 5) {
                   message.info('Đã chọn đủ 5 bài viết, không thể thêm nữa!');
                 } else {
                   message.info(`Đã chọn ${selectedArticles.length}/5 bài viết, có thể chọn thêm!`);
                 }
               }
             }}
          >
            {articles.map((article) => {
                             const isSelected = selectedArticles.includes(article._id);
               const isDisabled = !isSelected && selectedArticles.length >= 5;

              return (
                <Option
                  key={article._id}
                  value={article._id}
                  disabled={isDisabled}
                  title={article.title}
                >
                  <div className="flex items-center space-x-2">
                    <img
                      src={article.featuredImage?.url || defaultImageUrl}
                      alt={article.featuredImage?.alt || article.title}
                      className="object-cover w-6 h-6 rounded"
                      onError={(e) => {
                        e.currentTarget.src = defaultImageUrl;
                      }}
                    />
                    <span style={{ opacity: isDisabled ? 0.5 : 1 }}>
                      {article.title}
                    </span>
                    <Tag color="green" className="text-xs">{article.type}</Tag>
                    {isSelected && (
                      <Tag color="blue" className="text-xs">Đã chọn</Tag>
                    )}
                                         {isDisabled && !isSelected && (
                       <Tag color="red" className="text-xs">Đã đủ 5 bài</Tag>
                     )}
                  </div>
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      </Card>

      {/* Collections Section */}
      <Card
        title="Phần Bộ sưu tập nổi bật"
        style={{
          marginBottom: 24,
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Form.Item
          name="collections"
          label="Chọn bộ sưu tập (tối đa 5 bộ)"
          rules={[{ required: true, message: 'Vui lòng chọn ít nhất 1 bộ sưu tập' }]}
        >
          <Select
            mode="multiple"
            placeholder="Chọn bộ sưu tập"
            allowClear
            showSearch
            loading={collectionsLoading}
            maxTagCount={5}
            maxTagTextLength={20}
            value={selectedCollections}
            optionFilterProp="title"
            filterOption={(input, option) =>
              String((option as any)?.title || '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(values) => {
              // Cập nhật state local
              setSelectedCollections(values || []);

              // Giới hạn tối đa 5 bộ sưu tập
              if (values && values.length > 5) {
                const limitedValues = values.slice(0, 5);
                form.setFieldsValue({ collections: limitedValues });
                setSelectedCollections(limitedValues);
                message.warning('Chỉ có thể chọn tối đa 5 bộ sưu tập');
              }
              // Thông báo khi đủ 5 bộ sưu tập
              if (values && values.length === 5) {
                message.success('Đã chọn đủ 5 bộ sưu tập!');
              }
              // Thông báo khi xóa bộ sưu tập và có thể chọn thêm
              if (values && values.length < 5) {
                const currentValues = selectedCollections;
                if (currentValues.length > values.length) {
                  message.info('Đã xóa bộ sưu tập, có thể chọn thêm bộ sưu tập khác!');
                }
              }
            }}
            onDropdownVisibleChange={(open) => {
              if (open) {
                if (selectedCollections.length >= 5) {
                  message.info('Đã chọn đủ 5 bộ sưu tập, không thể thêm nữa!');
                } else {
                  message.info(`Đã chọn ${selectedCollections.length}/5 bộ sưu tập, có thể chọn thêm!`);
                }
              }
            }}
          >
            {collections.map((collection) => {
              const isSelected = selectedCollections.includes(collection._id);
              const isDisabled = !isSelected && selectedCollections.length >= 5;

              return (
                <Option
                  key={collection._id}
                  value={collection._id}
                  disabled={isDisabled}
                  title={collection.name}
                >
                  <div className="flex items-center space-x-2">
                    <img
                      src={collection.images?.[0] || defaultImageUrl}
                      alt={collection.name}
                      className="object-cover w-6 h-6 rounded"
                      onError={(e) => {
                        e.currentTarget.src = defaultImageUrl;
                      }}
                    />
                    <span style={{ opacity: isDisabled ? 0.5 : 1 }}>
                      {collection.name}
                    </span>
                    <Tag color="blue" className="text-xs">{collection.status}</Tag>
                    {isSelected && (
                      <Tag color="green" className="text-xs">Đã chọn</Tag>
                    )}
                    {isDisabled && !isSelected && (
                      <Tag color="red" className="text-xs">Đã đủ 5 bộ</Tag>
                    )}
                  </div>
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      </Card>

      {/* Newsletter Section */}
      {/* <Card title="Phần Newsletter" className="mb-6">
        <Form.Item
          name={['newsletter', 'title']}
          label="Tiêu đề"
          rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
        >
          <Input
            placeholder="VD: SIGN UP FOR NEWS LETTER"
            disabled
            style={{
              width: '100%',
              height: 32,
              borderRadius: 4,
              fontSize: 16,
              padding: '6px 12px',
            }}
          />
        </Form.Item>
        <Form.Item
          name={['newsletter', 'description']}
          label="Mô tả"
          rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
        >
          <TextArea
            rows={6}
            placeholder="VD: Đăng ký nhận thông tin mới nhất từ DE SILK."
            disabled
            style={{
              width: '100%',
              borderRadius: 4,
              fontSize: 16,
              padding: '6px 12px',
            }}
          />
        </Form.Item>
      </Card> */}

    </Form>
    
    {/* Fixed Action Buttons */}
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        borderTop: '1px solid #e8e8e8',
        padding: '16px 24px',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Space size="large">
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={saveLoading || loading}
          size="large"
        >
          Lưu cấu hình
        </Button>
        <Button onClick={handleReset} size="large">
          Làm mới
        </Button>
        <Button
          onClick={() => {
            const values = form.getFieldsValue();
            console.log('Current form values:', values);
            const jsonString = JSON.stringify(values, null, 2);
            alert('Dữ liệu JSON hiện tại:\n\n' + jsonString);
          }}
          size="large"
          disabled
        >
          Xem JSON
        </Button>
        <Button
          onClick={() => {
            try {
              const values = form.getFieldsValue();
              const jsonString = JSON.stringify(values, null, 2);
              JSON.parse(jsonString);
              message.success('Định dạng JSON hợp lệ!');
            } catch (e) {
              const errorMessage = e instanceof Error ? e.message : 'Lỗi không xác định';
              message.error('Định dạng JSON không hợp lệ: ' + errorMessage);
            }
          }}
          size="large"
          disabled
        >
          Kiểm tra JSON
        </Button>
      </Space>
    </div>
    </>
  );
};

export default HomeConfigForm;
